import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
// @mui
import { useTheme, styled } from '@mui/material/styles';
import {
  Stack,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Box,
  TextField,
  InputAdornment,
  Typography,
  Divider,
  MenuItem,
} from '@mui/material';
// hooks
import { useRouter, usePathname } from 'src/routes/hooks';
import { useOffSetTop } from 'src/hooks/use-off-set-top';
import { useResponsive } from 'src/hooks/use-responsive';
// components
import { Logo } from 'src/components/logo';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import Iconify from 'src/components/iconify/iconify';
import { useSettingsContext } from 'src/components/settings';

import { useDispatch, useSelector } from 'src/store';
import { signout } from 'src/store/reducers/auth';
import { set, remove } from 'src/store/reducers/search';
//

import { paths } from 'src/routes/paths';
import { LoginView, RegisterView } from 'src/sections/auth';
import { toCurrency } from 'src/utils/to-currency';
import { GiftIcon, MenuIcon } from 'src/assets/icons';

import { PATH_AFTER_LOGIN } from 'src/config-global';
import { HEADER } from '../config-layout';

import { NavToggleButton } from '../_common';

// ----------------------------------------------------------------------

const MenuButton = styled(Button)(({ theme }) => ({
  paddingLeft: 20,
  paddingRight: 20,
  borderRadius: 32,
  color: '#000',
  textTransform: 'uppercase',
  '&:hover': {
    boxShadow: '0 4px 8px rgba(0,0,0,.06),0 2px 0 rgba(0,0,0,.05)',
    background: 'rgba(145, 158, 171, 0.08)',
  },
  '&.active': {
    background: '#ffeb3b33',
  },
}));

type Props = {
  onOpenNav?: VoidFunction;
};

export default function Header({ onOpenNav }: Props) {
  const { isLoggedIn, user } = useSelector((state) => state.auth);

  const [loginModalStatus, setLoginModalStatus] = useState(false);
  const [signupModalStatus, setSignupModalStatus] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const router = useRouter();
  const pathName = usePathname();
  const popover = usePopover();
  const popover1 = usePopover();

  const settings = useSettingsContext();

  const isNavHorizontal = settings.themeLayout === 'horizontal';
  // const isNavMini = settings.themeLayout === 'mini';

  const lgUp = useResponsive('up', 'lg');
  const isMobile = useResponsive('down', 'sm');

  const offset = useOffSetTop(HEADER.H_DESKTOP);

  const offsetTop = offset && !isNavHorizontal;

  const [isSearch, setIsSearch] = useState(false);
  const [searchGame, setSearchGame] = useState('');

  const handleCloseSearch = () => {
    setIsSearch(false);
    setSearchGame('');
    dispatch(remove());
  };

  const handleSearchGame = (event: any) => {
    setSearchGame(event.target.value);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      dispatch(set(searchGame));
    }
  };

  const handleLogout = async () => {
    try {
      localStorage.clear();
      dispatch(signout());
      popover.onClose();
      navigate(PATH_AFTER_LOGIN);
    } catch (error) {
      console.error(error);
    }
  };

  // const handleClickItem = (path: string) => {
  //   popover.onClose();
  //   router.push(path);
  // };

  const renderContent = (
    <>
      <Stack alignItems="center" direction="row" zIndex={100} gap={{ md: 2, xs: 0 }}>
        {!lgUp ? (
          <MenuIcon style={{ cursor: 'pointer', width: 43 }} onClick={onOpenNav} />
        ) : (
          <NavToggleButton />
        )}

        <Logo
          sx={{
            zIndex: 100,
          }}
        />
      </Stack>

      <Stack
        direction="row"
        gap={0.5}
        height={1}
        p={0}
        ml={1}
        sx={{ color: '#fff', textTransform: 'uppercase' }}
      >
        <Stack
          alignItems="center"
          justifyContent="center"
          onClick={() => router.push(paths.dashboard.casino)}
          sx={{
            position: 'relative',
            width: '70px',
            cursor: 'pointer',
            paddingTop: { md: '10px' },
            ...(pathName === paths.dashboard.casino && {
              '& ::after': {
                content: '" "',
                width: 1,
                height: 2,
                background: '#fff',
                position: 'absolute',
                left: 0,
                bottom: 0,
              },
            }),
          }}
        >
          <Typography fontWeight={500}>Casino</Typography>
        </Stack>
        <Stack
          alignItems="center"
          justifyContent="center"
          onClick={() => router.push(paths.dashboard.liveCasino)}
          sx={{
            position: 'relative',
            width: '70px',
            cursor: 'pointer',
            paddingTop: { md: '10px' },
            ...(pathName === paths.dashboard.liveCasino && {
              '& ::after': {
                content: '" "',
                width: 1,
                height: 2,
                background: '#fff',
                position: 'absolute',
                left: 0,
                bottom: 0,
              },
            }),
          }}
        >
          <Typography fontWeight={500}>Live</Typography>
        </Stack>
      </Stack>

      <Stack direction="row" justifyContent="flex-end" alignItems="center" flex={1}>
        {lgUp && (
          <Box
            sx={{
              display: 'flex',
              width: { xs: '218px', sm: 'auto' },
              minHeight: '46px',
              gap: 1,
              p: 0.5,
              borderRadius: 4,
              ...(isMobile && {
                justifyContent: 'flex-end',
              }),
            }}
          >
            {isSearch ? (
              <TextField
                value={searchGame}
                onChange={handleSearchGame}
                onKeyDown={handleKeyPress}
                placeholder="Search for game"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="eva:search-fill" sx={{ color: '#fff' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Iconify
                        icon="ic:round-close"
                        onClick={handleCloseSearch}
                        sx={{
                          cursor: 'pointer',
                          color: '#fff',
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: '100%',
                  color: '#fff',
                  '& .MuiInputBase-root': { borderRadius: 4 },
                  '& input': { color: '#fff', py: 1, '&::placeholder': { color: '#fff' } },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#fff',
                  },
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#fff !important',
                    },
                    borderColor: '#fff',
                  },
                }}
              />
            ) : (
              <MenuButton
                sx={{
                  px: 1.25,
                  minWidth: '40px',
                }}
                onClick={() => {
                  setIsSearch(true);
                }}
              >
                <Iconify icon="eva:search-fill" color="#fff" />
              </MenuButton>
            )}
          </Box>
        )}

        {isLoggedIn ? (
          <Stack direction="row" gap={1} zIndex={100}>
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              p={0.4}
              pl={1}
              pr={{ xs: 1, md: 0.5 }}
              sx={{
                border: 'solid 0.5px #e1e1e1',
                background: '#fff',
                borderRadius: 4,
                height: 35,
                width: 'auto',
                cursor: 'pointer',
              }}
              onClick={popover1.onOpen}
            >
              <Typography color="#090915" lineHeight="initial">
                {toCurrency(user.balance || 0, user.currency)}
              </Typography>
              {!isMobile && (
                <Button
                  variant="soft"
                  sx={{
                    minWidth: '20px',
                    width: 25,
                    height: 25,
                    borderRadius: 4,
                    p: 0,
                  }}
                >
                  <GiftIcon width={20} height={20} color="#ff3c00" />
                </Button>
              )}
            </Stack>
            <CustomPopover
              open={popover1.open}
              onClose={popover1.onClose}
              sx={{
                width: 400,
                p: 1,
                mt: '3px',
                background: '#fff',
                '& span': { display: 'none' },
              }}
            >
              <Stack direction="row" gap={0.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  p={1}
                  width="50%"
                  sx={{ background: '#f6f7f8', borderRadius: '10px 0px 0px 10px' }}
                >
                  <Box
                    component="img"
                    src="/assets/images/home/real-money.png"
                    width={35}
                    height={35}
                  />
                  <Box>
                    <Typography fontSize="12px" fontFamily="cursive">
                      Real money
                    </Typography>
                    <Box>{toCurrency(user?.balance || 0, user.currency)}</Box>
                  </Box>
                </Stack>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  p={1}
                  width="50%"
                  sx={{ background: '#f6f7f8', borderRadius: '0px 10px 10px 0px' }}
                >
                  <Box
                    component="img"
                    src="/assets/images/home/bonus-money.png"
                    width={35}
                    height={35}
                  />
                  <Box>
                    <Typography fontSize="12px" fontFamily="cursive">
                      Bonus money
                    </Typography>
                    <Box>
                      {toCurrency((user?.bonus || 0) + (user?.matchBonus || 0), user.currency)}
                    </Box>
                  </Box>
                </Stack>
              </Stack>

              <Button
                sx={{ fontWeight: 'fontWeightBold', background: '#f6f7f8', width: '100%', my: 1 }}
              >
                <GiftIcon width={20} height={20} color="red" />
                Deposit
              </Button>
            </CustomPopover>

            <IconButton
              sx={{
                color: '#fff',
                zIndex: 100,
                height: 35,
                width: 35,
                '&:hover': { background: '#ee2b25' },
              }}
              onClick={popover.onOpen}
            >
              <Iconify icon="mingcute:user-1-fill" />
            </IconButton>
            <CustomPopover
              open={popover.open}
              onClose={popover.onClose}
              sx={{
                width: 300,
                p: 1,
                mt: '3px',
                background: '#fff',
                '& span': { display: 'none' },
              }}
            >
              <Box sx={{ background: '#f6f7f8', borderRadius: '10px' }}>
                <Box sx={{ p: 2, pb: 1.5 }}>
                  <Typography variant="subtitle2" noWrap>
                    {user?.name}
                  </Typography>

                  <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
                    {user?.email}
                  </Typography>
                </Box>

                <Divider sx={{ borderStyle: 'dashed' }} />

                {/* <Stack sx={{ p: 1 }}>
                  {OPTIONS.map((option) => (
                    <MenuItem key={option.label} onClick={() => handleClickItem(option.linkTo)}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Stack> */}

                <Divider sx={{ borderStyle: 'dashed' }} />

                <MenuItem
                  onClick={handleLogout}
                  sx={{ fontWeight: 'fontWeightBold', color: 'error.main', p: 2 }}
                >
                  Logout
                </MenuItem>
              </Box>
            </CustomPopover>
          </Stack>
        ) : (
          <Box>
            <Button
              sx={{
                border: 'solid 1px #ededee',
                color: '#fff',
                borderRadius: 4,
                zIndex: 100,
                '&:hover': { background: 'rgb(184 197 224 / 15%)' },
              }}
              onClick={() => setLoginModalStatus(true)}
            >
              Log in
            </Button>
            {!isMobile && (
              <Button
                sx={{
                  borderRadius: 4,
                  background: '#37a25c',
                  marginLeft: 1,
                  zIndex: 100,
                  color: '#fff',
                  '&:hover': { background: '#2d864c' },
                }}
                onClick={() => setSignupModalStatus(true)}
              >
                Sign up
              </Button>
            )}
          </Box>
        )}
      </Stack>

      <LoginView status={loginModalStatus} onCloseModal={() => setLoginModalStatus(false)} />
      <RegisterView status={signupModalStatus} onCloseModal={() => setSignupModalStatus(false)} />
    </>
  );

  return (
    <AppBar
      sx={{
        height: HEADER.H_MOBILE,
        zIndex: theme.zIndex.appBar + 1,
        boxShadow: '0 .5rem 1rem 1px rgba(0, 0, 0, .15)',
        transition: theme.transitions.create(['height'], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(lgUp && {
          height: HEADER.H_DESKTOP_OFFSET,
          ...(offsetTop && {
            height: HEADER.H_DESKTOP_OFFSET,
          }),
          ...(isNavHorizontal && {
            width: 1,
            bgcolor: 'background.default',
            height: HEADER.H_DESKTOP_OFFSET,
            borderBottom: `dashed 1px ${theme.palette.divider}`,
          }),
        }),
        backgroundColor: '#ff3c00',
      }}
    >
      <Toolbar
        sx={{
          height: 1,
          justifyContent: 'space-between',
          px: { xs: 1, md: 2 },
        }}
      >
        {renderContent}
      </Toolbar>
    </AppBar>
  );
}
